import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ConversationEvalSampleCreate,
  ConversationEvalSampleFromConversation,
} from '@frontend/types';

import {
  createConversationEvaluationSample,
  createConversationSampleFromConversation,
  deleteConversationEvaluationSample,
  getConversationEvaluationSamples,
  getSpecializedAgentKey,
  updateConversationEvaluationSample,
  updateEvaluationCriteria,
} from '../services/specialized-agent';

export function useGetConversationEvaluationSamples(
  instanceId: string,
  agentId: string,
  enabled = true
) {
  return useQuery({
    queryKey: getConversationEvaluationSamples.key(instanceId, agentId),
    queryFn: getConversationEvaluationSamples(instanceId, agentId),
    enabled: enabled && !!instanceId && !!agentId,
  });
}

export function useCreateConversationEvaluationSample(instanceId: string, agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ConversationEvalSampleCreate) =>
      createConversationEvaluationSample(instanceId, agentId, data)(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getConversationEvaluationSamples.key(instanceId, agentId),
      });
    },
  });
}

export function useCreateConversationSampleFromConversation(instanceId: string, agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ConversationEvalSampleFromConversation) =>
      createConversationSampleFromConversation(instanceId, agentId, data)(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getConversationEvaluationSamples.key(instanceId, agentId),
      });
    },
  });
}

export function useUpdateConversationEvaluationSample(instanceId: string, agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sampleId,
      data,
    }: {
      sampleId: string;
      data: Partial<ConversationEvalSampleCreate>;
    }) => updateConversationEvaluationSample(instanceId, agentId, sampleId, data)(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getConversationEvaluationSamples.key(instanceId, agentId),
      });
    },
  });
}

export function useDeleteConversationEvaluationSample(instanceId: string, agentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sampleId: string) =>
      deleteConversationEvaluationSample(instanceId, agentId, sampleId)(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getConversationEvaluationSamples.key(instanceId, agentId),
      });
    },
  });
}

export function useUpdateEvaluationCriteria(instanceId: string, agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (criteria: string[]) => updateEvaluationCriteria(instanceId, agentId, criteria)(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getSpecializedAgentKey(agentId),
      });
    },
  });
}
